import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Button,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useToast,
  Container,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Icon,
  useDisclosure
} from '@chakra-ui/react';
import { ChevronDownIcon, AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import CreateProjectModal from '../project/CreateProjectModal';

const AuthenticatedLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load projects from API
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/v1/projects');
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast({
        title: 'Error loading projects',
        description: 'Failed to load your projects',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: 'Logged out successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate('/login');
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" borderBottom="1px" borderColor="gray.200" px={6} py={4}>
        <Flex justify="space-between" align="center">
          <HStack spacing={8}>
            <Heading size="md" color="blue.600">
              D360 Platform
            </Heading>
            <Button variant="ghost" colorScheme="blue">
              Projects
            </Button>
          </HStack>

          <HStack spacing={4}>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size="sm"
              onClick={onCreateModalOpen}
            >
              New Project
            </Button>

            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={<ChevronDownIcon />}
                leftIcon={<Avatar size="sm" name={user?.name} />}
              >
                {user?.name}
              </MenuButton>
              <MenuList>
                <MenuItem>Profile</MenuItem>
                <MenuItem>Settings</MenuItem>
                <MenuDivider />
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Box>

      {/* Main Content */}
      <Container maxW="6xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>
              Your Projects
            </Heading>
            <Text color="gray.600">
              Create and manage your 3D/2D design projects
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {isLoading ? (
              <Box gridColumn="1 / -1" textAlign="center" py={8}>
                <Text>Loading projects...</Text>
              </Box>
            ) : projects.length === 0 ? (
              <Box gridColumn="1 / -1" textAlign="center" py={8}>
                <VStack spacing={4}>
                  <Text color="gray.500">No projects found</Text>
                  <Text fontSize="sm" color="gray.400">
                    Create your first project to get started
                  </Text>
                </VStack>
              </Box>
            ) : (
              projects.map((project) => (
                <Card
                  key={project.id}
                  cursor="pointer"
                  _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  <CardHeader pb={2}>
                    <Heading size="md">{project.name}</Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Text color="gray.600" mb={4}>
                      {project.description}
                    </Text>
                    <HStack spacing={2}>
                      <Button size="sm" colorScheme="blue" variant="outline">
                        2D View
                      </Button>
                      <Button size="sm" colorScheme="green" variant="outline">
                        3D View
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>
              ))
            )}

            {/* New Project Card */}
            <Card
              cursor="pointer"
              _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
              transition="all 0.2s"
              borderStyle="dashed"
              borderWidth="2px"
              borderColor="gray.300"
              bg="transparent"
              onClick={onCreateModalOpen}
            >
              <CardBody
                display="flex"
                alignItems="center"
                justifyContent="center"
                minH="200px"
              >
                <VStack spacing={3}>
                  <Icon as={AddIcon} w={8} h={8} color="gray.400" />
                  <Text color="gray.600" fontWeight="medium">
                    Create New Project
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        onSuccess={(newProject) => {
          toast({
            title: 'Project created successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          // Refresh the projects list
          loadProjects();
        }}
      />
    </Box>
  );
};

export default AuthenticatedLayout; 